import { Link } from 'react-router-dom';
import { SectionLabel, TiltCard } from '../components/Effects';
import { ArrowIcon } from '../components/Icons';

export function JourneyPage() {
  const milestones = [
    {
      era: "CHAPTER I",
      date: "YEAR 2021 - 2022",
      title: "The Genesis & Uge Appage SMP Reborn",
      summary: "A humble survival world born from friendship and spontaneous late-night adventures.",
      desc: "It began with a ragtag crew of Sri Lankan gamers seeking a genuine multiplayer survival experience. No strict rules, just pure Minecraft nostalgia, impromptu town squares, and hilarious rivalries that laid the cultural foundation of Uge Appage SMP.",
      badge: "SEASON 1 / REBORN"
    },
    {
      era: "CHAPTER II",
      date: "YEAR 2022 - 2023",
      title: "Rise of No Port City & Faction Politics",
      summary: "First major territorial settlements, cross-continental shipping routes, and faction treaties.",
      desc: "As the community swelled, players moved past nomadic survival. No Port City emerged as a commercial capital. Strategic harbor fortifications, trade wars, and territorial disputes proved that this was no ordinary server—it was a burgeoning player society.",
      badge: "SEASON 2 / FRONTIER"
    },
    {
      era: "CHAPTER III",
      date: "SEPTEMBER 2026",
      title: "Season III: The Sovereign Civilization",
      summary: "The 1 Billion LKR monetary economy, Old Town & New Town dichotomy, and patent industries.",
      desc: "Season 3 reimagined everything. We engineered a closed-loop macroeconomic system: physical wallets, progressive wealth tax, 16 registered industrial patents, and offshore biosphere ocean yards. Java and Bedrock crossplay united the nation under a single currency.",
      badge: "SEASON 3 / CURRENT ERA"
    }
  ];

  return (
    <div className="journey-page">
      <section className="page-hero">
        <div className="page-hero-bg" />
        <div className="page-hero-copy">
          <div className="eyebrow">
            <span /> HISTORICAL ARCHIVE / OUR JOURNEY
          </div>
          <h1>
            The Epic Tale<br />
            <em>of Our People.</em>
          </h1>
          <p>
            From an accidental livestream title and a lone wooden mansion to a 1 Billion LKR sovereign economy.
            This is the raw, uncut history of Uge Appage SMP.
          </p>
          <div className="page-hero-actions">
            <a className="outline-link" href="#chronicle-memoir">
              Read The Origin Story <ArrowIcon />
            </a>
            <a className="ghost-link" href="/#episodes">
              Watch The Episodes <ArrowIcon />
            </a>
          </div>
        </div>
        <div className="constitution-seal">
          <img src="/assets/ugeappage-logo-zoomed.webp" alt="Uge Appage SMP Seal" />
          <span>EST. 2021</span>
          <b>CHRONICLE</b>
          <small>AN UNDERRATED SAGA</small>
        </div>
      </section>

      {/* Narrative Memoir Section */}
      <section id="chronicle-memoir" className="section-pad origin-memoir-section">
        <SectionLabel index="ORIGIN">THE MEMOIR</SectionLabel>
        <div className="split-heading" data-reveal>
          <h2>The Genesis of a Brotherhood:<br /><em>How "Uge Appage" Was Born</em></h2>
          <p>
            Before the automated trading complexes, sovereign vaults, and trans-mountain railways, there was only a brotherhood of dreamers.
          </p>
        </div>

        <div className="memoir-parchment-card" data-reveal>
          <div className="memoir-header">
            <div className="memoir-badge">ARCHIVAL FOLIO • THE UNTOLD CHRONICLES</div>
            <span className="memoir-date">Circa 2021 — Ceylon to Cyberspace</span>
          </div>

          <div className="memoir-prose">
            <p className="memoir-lead">
              <span className="drop-cap">I</span>t did not begin in a corporate boardroom or through a grand masterplan.
              The moniker <strong>"Uge Appage"</strong> was struck like a rogue spark in the dark when <strong>Hiru X Gaming (Hirusha Udayanga)</strong> typed a spontaneous, tongue-in-cheek caption for a solo Minecraft survival livestream. He was playing alone—carving dirt and stone under the flickering glow of his screen—unaware that those words would echo across years and bind a brotherhood together.
            </p>

            <p>
              Around that broadcast, a close circle of friends gravitated like moths to a flame. Voices like <strong>Anshika Gaming</strong>, <strong>SL_Tharu</strong>, <strong>Jabuu.x</strong>, <strong>Senura LK</strong>, <strong>Shashindu</strong>, <strong>Ransilu</strong>, <strong>Chama bro</strong>, <strong>MR.SARA</strong>, and a handful of passionate companions began kicking an intoxicating idea back and forth: <em>Why wander in single-player solitude when we could conquer a shared world together?</em>
            </p>

            <p>
              That spark ignited into reality when our generous friend <strong>Chalana Gimhana (Founder of NetchLK)</strong> stepped forward. At his own personal expense, he sponsored and provisioned our very first dedicated server. We spawned into raw, untamed wilderness and built our first communal shelter—baptized with pride and laughter as <strong>"Uge Appage Maha Gedara"</strong> (<em>The Grand Ancestral Estate</em>).
            </p>

            <p>
              Before long, the name stuck like resin to pine. The realm naturally became known as <strong>Uge Appage SMP — Season 1</strong>. We wove deep, cinematic, intertwining lore—humble stories that felt like an indie comic-book universe where every player was a hero in their own right. We were never mainstream, nor did we chase fleeting internet virality. We were an underrated sanctuary for genuine storytellers.
            </p>

            <p>
              A year of relentless adventures flowed by before the first sunset. When we unveiled <strong>Season 2</strong>, fresh faces crossed our borders. It was then we realized something profoundly humbling: our little SMP wasn't just a video game server anymore. A quiet, devoted circle of players truly cherished this pocket universe we had cultivated.
            </p>

            <p>
              Then, life did what it inevitably does. Players graduated, entered university halls, took on demanding campus workloads, and stepped into high-pressure careers. Real-world adulthood took hold, and the keyboards fell quiet. The world went dark for a long time.
            </p>

            <p>
              Yet brotherhood never truly sleeps. An impromptu, late-night conversation among old friends reignited the dormant embers. <em>"Should we bring Uge Appage SMP back?"</em> In an instant, that old familiar fire rushed through our veins.
            </p>

            <p>
              Stepping up to turn that nostalgic dream into modern reality, <strong>Jabuu.x (WJ Biman)</strong> took command of server hosting, engineering, and architectural development. Under his technical leadership, <strong>Uge Appage SMP Season 3</strong> was born—not just revived, but revolutionized. Alongside the dedicated Staff team, they crafted macroeconomic systems never before witnessed in a Minecraft realm: a 1 Billion LKR reserve, sovereign physical wallets, industrial patents, and living player-run economies.
            </p>

            <p className="memoir-heart">
              Looking back across the years, our hearts swell with immense pride. We know that to the vast expanse of the internet, we are still known only to a tight-knit few. But to every single wanderer, builder, and warrior who ever logged in, placed a single torch, or shared a laugh with us—even for just one solitary day—we offer you our deepest, eternal gratitude.
            </p>

            <p className="memoir-closing">
              The road behind us is unforgettable, but the horizon ahead is infinite. We still have a legendary journey to walk, and we hope you walk it side-by-side with us.
            </p>
          </div>

          <div className="memoir-signoff">
            <div className="signoff-quote">"Happy Gaming."</div>
            <div className="signoff-author">
              <strong>Uge Appage SMP Management & Staff</strong>
              <span>With Love & Brotherhood</span>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Milestones */}
      <section className="section-pad">
        <SectionLabel index="TIMELINE">CIVILIZATION MILESTONES</SectionLabel>
        <div className="split-heading" data-reveal>
          <h2>Three Eras of<br /><em>Endurance & Triumph.</em></h2>
          <p>Every era left scars, monuments, and stories told around campfires in Old Town. Here is how we got here.</p>
        </div>

        <div className="timeline-container" data-reveal>
          {milestones.map((m, idx) => (
            <div key={m.era} className="timeline-item">
              <div className="timeline-marker">
                <span className="marker-dot" />
                <span className="marker-line" />
              </div>
              <TiltCard className="timeline-card">
                <div className="timeline-card-header">
                  <span className="timeline-era">{m.era} · {m.date}</span>
                  <span className="timeline-badge">{m.badge}</span>
                </div>
                <h3>{m.title}</h3>
                <h4 className="timeline-summary">{m.summary}</h4>
                <p className="timeline-desc">{m.desc}</p>
                <div className="timeline-footer">
                  <small>ARCHIVE INDEX #{idx + 1}</small>
                  <a href="/#episodes" className="inline-watch-link">Watch live streams of this era →</a>
                </div>
              </TiltCard>
            </div>
          ))}
        </div>
      </section>

      <section className="section-pad rules-pledge">
        <div data-reveal>
          <img src="/assets/ugeappage-logo-zoomed.webp" alt="Uge Appage SMP" />
          <small>BE PART OF CHAPTER V</small>
          <h2>The next chapter is<br />yours to write.</h2>
          <p>Our history isn't written by admins—it's forged by players who dare to explore, build, and conquer.</p>
          <div className="pledge-actions">
            <Link className="primary-btn enchant" to="/">Apply For Whitelist <ArrowIcon /></Link>
            <Link className="outline-link" to="/rules">Server Constitution <ArrowIcon /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
